import { PagingParameters } from "@/api/dtos";
import { generateTable } from "@/config/dashboard/general";
import {
  multipleprintsColumns,
  multipleprintsLinksColumnsVisibility,
} from "@/config/dashboard/multipleprints";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { Multipleprint } from "@/lib/types";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { DataTable } from "../ui/datatable";
import { DatatablePaginationProps } from "../ui/datatable-pagination";
import { Input } from "../ui/input";
import { GridCardWithActions } from "./GridCardWithActions";
import { MultipleprintLinkGridCard } from "./relatedLinkGridCards";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";

export interface MultipleprintLinksViewProps {
  storageKey: string;
  title: string;
  backLabel?: string;
  /** Multiples déjà liés (page courante) et leur pagination. */
  linked: Multipleprint[];
  linkedPagination: DatatablePaginationProps;
  /** Multiples proposés en mode ajout et leur pagination. */
  candidates: Multipleprint[];
  candidatesTotalSize: number;
  candidatesPagination: PagingParameters;
  setCandidatesPagination: Dispatch<SetStateAction<PagingParameters>>;
  searchContent: string;
  setSearchContent: (value: string) => void;
  onAdd: (multipleprintId: string) => void;
  onDelete: (multipleprintId: string) => void;
}

/**
 * Affichage commun des liens vers des multiples (liste / grille / mode ajout) :
 * « Enregistrements liés › Multiple » et onglet Portfolio. Les données viennent du parent.
 */
export function MultipleprintLinksView({
  storageKey,
  title,
  backLabel,
  linked,
  linkedPagination,
  candidates,
  candidatesTotalSize,
  candidatesPagination,
  setCandidatesPagination,
  searchContent,
  setSearchContent,
  onAdd,
  onDelete,
}: MultipleprintLinksViewProps) {
  const { t } = useTranslation();

  const [isAddMode, setIsAddMode] = useState(false);
  const toggleAddMode = () => setIsAddMode((value) => !value);
  const [selectedMultipleprintId, setSelectedMultipleprintId] = useState<
    string | null
  >(null);

  const previewElements = useCallback(
    (item: Multipleprint) => [
      {
        label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        value: item.estampesMultiplesDescriptionNumeroEnregistrement,
        type: "text" as const,
      },
      {
        label: t("estampesMultiplesDescriptionResume"),
        value: item.estampesMultiplesDescriptionResume,
        type: "richtext" as const,
      },
      {
        label: t("attachments"),
        value: item.attachments,
        type: "image" as const,
      },
    ],
    [t],
  );

  const renderRowActions = useCallback(
    (item: Multipleprint) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={item.id}
        previewType="multipleprint"
        previewElements={previewElements(item)}
        onDelete={() => onDelete(item.id)}
        onAdd={() => onAdd(item.id)}
      />
    ),
    [isAddMode, onAdd, onDelete, previewElements],
  );

  const baseColumns = useMemo(
    () => generateTable(multipleprintsColumns, "multipleprint", t),
    [t],
  );
  const columns: ColumnDef<Multipleprint, any>[] = useMemo(
    () => [
      ...baseColumns,
      createActionsColumnDef<Multipleprint>(({ row }) =>
        renderRowActions(row.original),
      ),
    ],
    [baseColumns, renderRowActions],
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      return readTablePreferences(storageKey)?.order || [];
    } catch {
      return [];
    }
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const visibility = readTablePreferences(storageKey)?.visibility;
        if (visibility && typeof visibility === "object")
          return { ...multipleprintsLinksColumnsVisibility, ...visibility };
      } catch {
        // ignore
      }
      return { ...multipleprintsLinksColumnsVisibility };
    },
  );
  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={storageKey}
      title={title}
      backLabel={backLabel}
      isAddMode={isAddMode}
      onBack={toggleAddMode}
      onToggleAdd={toggleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={candidates}
          pagination={{
            pagination: candidatesPagination,
            setPage: setCandidatesPagination,
            totalSize: candidatesTotalSize,
          }}
          storageKey={storageKey}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={linked}
          pagination={linkedPagination}
          storageKey={storageKey}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {linked.map((row) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderRowActions(row)}>
                <MultipleprintLinkGridCard
                  item={row}
                  t={t}
                  onTextClick={() => setSelectedMultipleprintId(row.id)}
                />
              </GridCardWithActions>
              <ShowElements
                type="multipleprint"
                id={row.id}
                open={selectedMultipleprintId === row.id}
                onOpenChange={(open) => {
                  if (!open) setSelectedMultipleprintId(null);
                }}
                trigger={<span />}
                elements={previewElements(row)}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={linkedPagination}
    />
  );
}
